import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search, Users, X } from 'lucide-react';
import { Profile } from '../../../types';
import { rotuloFuncaoComercial } from '../../../src/lib/departamentos';

interface TransferirEmMassaModalProps {
    count: number;
    profiles: Profile[];
    salvando: boolean;
    onConfirm: (userId: string) => void;
    onClose: () => void;
}

const ROLE_LABEL: Record<string, string> = {
    adm: 'Administrador',
    gerente: 'Gerente',
    vendedor: 'Vendedor',
    analista: 'Analista',
    parceiro: 'Parceiro',
    gerente_parcerias: 'Gerente de Parcerias',
    vendedor_parceiro: 'Vendedor Parceiro',
};

const TransferirEmMassaModal: React.FC<TransferirEmMassaModalProps> = ({ count, profiles, salvando, onConfirm, onClose }) => {
    const [busca, setBusca] = useState('');
    const [sel, setSel] = useState('');

    const lista = useMemo(() => {
        const term = busca.trim().toLowerCase();
        return profiles
            .filter(p => (p as any).status !== 'inactive')
            .filter(p => !term || (p.full_name || '').toLowerCase().includes(term))
            .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    }, [profiles, busca]);

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                            <Users size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Transferir Negociações</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {count} {count === 1 ? 'negociação será transferida' : 'negociações serão transferidas'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-3 overflow-y-auto flex flex-col min-h-0">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            placeholder="Buscar responsável..."
                            autoFocus
                            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-1 -mx-1 px-1 max-h-[40vh]">
                        {lista.map(p => {
                            const isSel = String(p.id) === sel;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setSel(String(p.id))}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors border ${isSel
                                        ? 'bg-primary/5 border-primary/40'
                                        : 'bg-gray-50/60 dark:bg-gray-700/30 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700/60'}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                                        {(p.full_name || '—').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{p.full_name}</p>
                                        <p className="text-[11px] text-gray-400">{rotuloFuncaoComercial(p) || ROLE_LABEL[(p as any).role] || (p as any).role}</p>
                                    </div>
                                    {isSel && <Check size={16} className="text-primary shrink-0" />}
                                </button>
                            );
                        })}
                        {lista.length === 0 && <p className="text-center text-sm text-gray-400 py-6">Ninguém encontrado.</p>}
                    </div>

                    <button
                        onClick={() => sel && onConfirm(sel)}
                        disabled={!sel || salvando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                        <Check size={16} />
                        {salvando ? 'Transferindo...' : 'Confirmar transferência'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TransferirEmMassaModal;
